import actionTypes from '../actionTypes';
import api from 'src/utils/api';
import { scrollToPostId } from 'src/utils/scroll';
import { setAlert } from 'src/typescript/components/alert/actions';
import enrichDataObject from 'src/utils/enrichDataObject';
import config from 'config/config';
import Router from 'next/router';
import * as log from 'loglevel';

export const lockPost = (postId, communitySlug) => updatePostLock(postId, communitySlug, true);
export const unlockPost = (postId, communitySlug) => updatePostLock(postId, communitySlug, false);

export const updatePostLock = (postId, communitySlug, isLocked) => {
  return async (dispatch) => {
    dispatch({ type: actionTypes.UPDATE_POST_LOCK });
    try {
      const response = await api.auroraPut(`post/${postId}/lock`, {
        isLocked,
        groupCode: communitySlug,
      });
      const { data: { status } } = response;

      if (!!status && !status.success) {
        dispatch({ type: actionTypes.UPDATE_POST_LOCK_FAILURE, error: status });
        dispatch(setAlert(status.message));
        return;
      }

      dispatch({
        type: actionTypes.UPDATE_POST_LOCK_SUCCESS,
        isLocked,
      });
    } catch (ex) {
      log.error(ex);
      dispatch({ type: actionTypes.UPDATE_POST_LOCK_FAILURE, error: ex });
      dispatch(setAlert(ex.message));
    }
  };
};

export const togglePostPin = (postId, communitySlug) => {
  return async (dispatch, getState) => {
    dispatch({ type: actionTypes.UPDATE_POST_PIN });
    try {
      const response = await api.auroraPost(`post/${postId}/pin`, {
        groupCode: communitySlug,
      });
      const { data: { payload: { isPinned = false } = {}, status } } = response;

      if (!!status && !status.success) {
        dispatch({ type: actionTypes.UPDATE_POST_PIN_FAILURE, error: status });
        dispatch(setAlert(status.message));
        return;
      }

      dispatch({
        type: actionTypes.UPDATE_POST_PIN_SUCCESS,
        isPinned,
      });
      dispatch(setAlert(status.message, 'success'));
    } catch (ex) {
      log.error(ex);
      dispatch({ type: actionTypes.UPDATE_POST_PIN_FAILURE, error: ex });
      dispatch(setAlert(ex.message));
    }
  };
};

export const deletePostReply = (postId, superParentId, parentId, communitySlug, successMessage) => {
  return async (dispatch) => {
    dispatch({ type: actionTypes.DELETE_POST_REPLY });
    try {
      await api.del(`private/posts/${communitySlug}/${parentId}/responses/${postId}`, {
        params: {
          superParentId,
        },
      });

      dispatch(setAlert(successMessage, 'success'));
      return dispatch({
        type: actionTypes.DELETE_POST_REPLY_SUCCESS,
        postId,
      });
    } catch (ex) {
      log.error(ex);
      dispatch({ type: actionTypes.DELETE_POST_REPLY_FAILURE, error: ex });
      dispatch(setAlert(ex.message));
    }
  };
};

export const deletePost = (postId, communitySlug, superParentId, successMessage) => {
  return async (dispatch) => {
    dispatch({ type: actionTypes.DELETE_POST });
    try {
      const response = await api.auroraDel(`post/${postId}`, {
        params: {
          groupCode: communitySlug,
          id: postId,
          superParentId,
          parentType: 'posts',
        },
      });
      const { data: { status } } = response;

      if (!!status && !status.success) {
        dispatch({ type: actionTypes.DELETE_POST_FAILURE, error: status });
        dispatch(setAlert(status.message));
        return;
      }

      dispatch({
        type: actionTypes.DELETE_POST_SUCCESS,
        postId,
        superParentId,
      });
      if (!superParentId || superParentId === postId) {
        dispatch(setAlert(successMessage, 'success'));
        Router.push(
          `/community/communityHome?slug=${communitySlug}`,
          `/${communitySlug}`,
        );
      }
    } catch (ex) {
      log.error(ex);
      dispatch({ type: actionTypes.DELETE_POST_FAILURE, error: ex });
      dispatch(setAlert(ex.message));
    }
  };
};

export const assignBadges = (roleId, groupCode, userId) => {
  return async (dispatch) => {
    dispatch({ type:actionTypes.ASSIGN_BADGES });
    try {
      const response = await api.auroraPost('user-roles', {
        groupCode,
        userId,
        roleId,
      });
      const { data: { status } } = response;

      if (!!status && !status.success) {
        dispatch({ type: actionTypes.ASSIGN_BADGES_FAILURE, error: status });
        if (status.responseCode === '401002') {
          dispatch(setAlert(status.message));
          return;
        }
        return;
      }

      dispatch({
        type: actionTypes.ASSIGN_BADGES_SUCCESS,
      });
    } catch (ex) {
      dispatch({ type: actionTypes.ASSIGN_BADGES_FAILURE, error: ex });
      dispatch(setAlert(ex.message));
    }
  };
};

export const removeBadges = (roleId, groupCode, userId) => {
  return async (dispatch) => {
    dispatch({ type:actionTypes.REMOVE_BADGES });
    try {
      const response = await api.auroraDel('user-roles', {
        params: {
          roleId,
          groupCode,
          userId,
        },
      });
      const { data: { status } } = response;

      if (!!status && !status.success) {
        dispatch({ type: actionTypes.REMOVE_BADGES_FAILURE, error: status });
        if (status.responseCode === '401002') {
          dispatch(setAlert(status.message));
          return;
        }
        return;
      }

      dispatch({
        type: actionTypes.REMOVE_BADGES_SUCCESS,
      });
    } catch (ex) {
      dispatch({ type: actionTypes.REMOVE_BADGES_FAILURE, error: ex });
      dispatch(setAlert(ex.message));
    }
  };
};

export const restrictUser = (user, communitySlug, successMessage, alreadyRestrictedMessage) => {
  return async (dispatch, getState) => {
    dispatch({ type: actionTypes.RESTRICT_USER });
    try {
      const response = await api.auroraPost('user-roles', {
        groupCode: communitySlug,
        userId: user.id,
        roleId: config.roles.restricted,
      });
      const { data: { payload, status } } = response;

      if (!!status && !status.success) {
        dispatch({ type: actionTypes.RESTRICT_USER_FAILURE, error: status });
        if (status.responseCode === '401002') {
          dispatch(setAlert(status.message));
          return;
        }
        dispatch(setAlert(alreadyRestrictedMessage));
        return;
      }

      dispatch({
        type: actionTypes.RESTRICT_USER_SUCCESS,
      });
      dispatch(setAlert(successMessage, 'success'));
    } catch (ex) {
      log.error(ex);
      dispatch({ type: actionTypes.RESTRICT_USER_FAILURE, error: ex });
      dispatch(setAlert(ex.message));
    }
  };
};

export const reportPost = ({ reason, details, postId, communitySlug }) => {
  return async (dispatch) => {
    dispatch({ type: actionTypes.RESTRICT_USER });
    try {
      const response = await api.auroraPost(`post/${postId}/report`, {
        reason,
        comment: details,
        id: postId,
        groupCode: communitySlug,
        parentType: 'posts',
      });
      const { data: { status } } = response;

      if (!!status && !status.success) {
        dispatch({ type: actionTypes.RESTRICT_USER_FAILURE, error: status });
        dispatch(setAlert(status.message));
        return;
      }

      dispatch({
        type: actionTypes.RESTRICT_USER_SUCCESS,
      });
      return true; // returning boolean to display the confirmation screen
    } catch (ex) {
      log.error(ex);
      dispatch({ type: actionTypes.RESTRICT_USER_FAILURE, error: ex });
      dispatch(setAlert(ex.message));
    }
  };
};

export const likePost =
  (postId, superParentId, communitySlug) => updatePostLike(postId, superParentId, communitySlug, true);
export const unlikePost =
  (postId, superParentId, communitySlug) => updatePostLike(postId, superParentId, communitySlug, false);

export const updatePostLike = (postId, parentPostId, communitySlug, isLiked) => {
  return async (dispatch) => {
    dispatch({ type: actionTypes.LIKE_POST });
    try {
      const response = await api.auroraPost(`post/${postId}/rate`, {
        op: isLiked ? '+' : '-',
        superParentId: parentPostId,
        id: postId,
        groupCode: communitySlug,
        parentType: 'posts',
      });
      const { data: { payload: { hasRated }, status } } = response;

      if (!!status && !status.success) {
        dispatch({ type: actionTypes.LIKE_POST_FAILURE, error: status });
        dispatch(setAlert(status.message));
        return;
      }

      dispatch({
        type: actionTypes.LIKE_POST_SUCCESS,
        isLiked: hasRated,
        postId,
        parentPostId,
        communitySlug,
      });
    } catch (ex) {
      log.error(ex);
      dispatch({ type: actionTypes.LIKE_POST_FAILURE, error: ex });
      dispatch(setAlert(ex.message));
    }
  };
};

export const followPost = (postId, trackLabel, successMessage) => {
  return async (dispatch, getState) => {
    dispatch({ type: actionTypes.FOLLOW_POST });
    try {
      const response = await api.auroraPost('follow/post', {
        Ids: [postId],
      });
      const { data: { payload, status } } = response;

      if (!!status && !status.success) {
        dispatch({ type: actionTypes.FOLLOW_POST_FAILURE, error: status });
        dispatch(setAlert(status.message));
        return;
      }

      dispatch({
        type: actionTypes.FOLLOW_POST_SUCCESS,
        postId,
        trackLabel,
      });
      if (successMessage) {
        dispatch(setAlert(successMessage, 'success'));
      }
    } catch (ex) {
      log.error(ex);
      dispatch({ type: actionTypes.FOLLOW_POST_FAILURE, error: ex });
      dispatch(setAlert(ex.message));
    }
  };
};

export const unfollowPost = (postId, successMessage) => {
  return async (dispatch, getState) => {
    dispatch({ type: actionTypes.UNFOLLOW_POST });
    try {
      const response = await api.auroraDel('follow/post', {
        params: {
          Ids: [postId],
        },
      });
      const { data: { payload, status } } = response;

      if (!!status && !status.success) {
        dispatch({ type: actionTypes.UNFOLLOW_POST_FAILURE, error: status });
        dispatch(setAlert(status.message));
        return;
      }

      dispatch({
        type: actionTypes.UNFOLLOW_POST_SUCCESS,
        postId,
      });
      dispatch(setAlert(successMessage, 'success'));
    } catch (ex) {
      log.error(ex);
      dispatch({ type: actionTypes.UNFOLLOW_POST_FAILURE, error: ex });
      dispatch(setAlert(ex.message));
    }
  };
};

export const respondPost = ({
  postId, superParentId, body, communitySlug, mentionedUsers = [],
  imageHash = null, imageDescription = null,
}) => {
  return async (dispatch) => {
    dispatch({ type: actionTypes.REPLY_POST });
    try {
      const { data } = await api.post(`private/posts/${communitySlug}/${postId}/responses`, {
        superParentId,
        body,
        mentionedUserIds: mentionedUsers.map(x => x.userId),
        imageHash,
        imageDescription,
      });

      const post = enrichDataObject(data, data.mentionedUsers);

      await dispatch({
        type: actionTypes.REPLY_POST_SUCCESS,
        post,
        communitySlug,
      });
      scrollToPostId(post.id);

      return { postId: post.id };
    } catch (ex) {
      log.error(ex);
      dispatch({ type: actionTypes.REPLY_POST_FAILURE, error: ex });
      dispatch(setAlert(ex.message));
    }
  };
};

export const editPostReply = ({
  postId, superParentId, parentId, body, communitySlug, successMessage,
  mentionedUsers = [], imageHash = null, imageDescription = null,
}) => {
  return async (dispatch) => {
    dispatch({ type: actionTypes.UPDATE_POST_REPLY });
    try {
      const { data } = await api.put(`private/posts/${communitySlug}/${parentId}/responses/${postId}`, {
        superParentId,
        body,
        mentionedUserIds: mentionedUsers.map(x => x.userId),
        imageHash,
        imageDescription,
      });

      const post = enrichDataObject(data, data.mentionedUsers);

      dispatch(setAlert(successMessage, 'success'));
      return dispatch({
        type: actionTypes.UPDATE_POST_REPLY_SUCCESS,
        post,
      });
    } catch (ex) {
      log.error(ex);
      dispatch({ type: actionTypes.UPDATE_POST_REPLY_FAILURE, error: ex });
      dispatch(setAlert(ex.message));
    }
  };
};

export const showSecondPageBlockMessageBox = () => {
  return (dispatch) => {
    dispatch({ type: actionTypes.SHOW_SECOND_PAGE_BLOCK_MESSAGE_BOX });
  };
};

export const showLoginBlockMessageBox = (username) => {
  return (dispatch) => {
    dispatch({
      type: actionTypes.SHOW_LOGIN_BLOCK_MESSAGE_BOX,
      username,
    });
  };
};
